import React, { FC, useContext } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { trackEvent } from 'dibs-tracking';
import { SeoLink } from 'dibs-seo/exports/SeoLink';
import classNames from 'classnames';

import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { SbRespTilesSwiper } from '../SbRespTilesSwiper/SbRespTilesSwiper';
import { SbRespDesignsDescriptionTile } from './SbRespDesignsDescriptionTile';
import { SbRespDesignTiles_itemSearch } from './__generated__/SbRespDesignTiles_itemSearch.graphql';

import styles from './SbRespDesignTiles.scss';

type TileType = NonNullable<SbRespDesignTiles_itemSearch['creatorDesignTiles']>[0];

type Props = {
    itemSearch: SbRespDesignTiles_itemSearch;
};

const FULL_ROW_LENGTH = 4;

const SbRespDesignTilesComponent: FC<Props> = ({ itemSearch }) => {
    const { creatorDesignTiles = [], displayUriRef } = itemSearch;
    const { isTablet } = useContext(ServerVarsContext);
    if (!creatorDesignTiles || creatorDesignTiles.length < 2) {
        return null;
    }

    return (
        <div
            className={classNames(styles.container, {
                [styles.hasFullRowOfTiles]: creatorDesignTiles.length === FULL_ROW_LENGTH,
            })}
        >
            <SbRespTilesSwiper displayUriRef={displayUriRef || ''} isTablet={isTablet}>
                <SbRespDesignsDescriptionTile itemSearch={itemSearch} />
                {creatorDesignTiles.map((tile: TileType, index: number) => {
                    const { displayName, linkData, imageUrl } = tile || {};
                    if (!displayName || !linkData || !imageUrl) {
                        return null;
                    }
                    return (
                        <SeoLink
                            className={styles.tile}
                            linkData={linkData}
                            key={displayName}
                            dataTn={`design-tile-link-${index}`}
                            onClick={() =>
                                trackEvent({
                                    category: 'promo interaction',
                                    action: 'more from iconic design creator clicked',
                                    label: 'creator page',
                                })
                            }
                        >
                            <div className={styles.imageWrapper}>
                                <img className={styles.image} src={imageUrl} alt={displayName} />
                            </div>
                            <div className={styles.title}>{displayName}</div>
                        </SeoLink>
                    );
                })}
            </SbRespTilesSwiper>
        </div>
    );
};

export const SbRespDesignTiles = createFragmentContainer(SbRespDesignTilesComponent, {
    itemSearch: graphql`
        fragment SbRespDesignTiles_itemSearch on ItemSearchQueryConnection {
            ...SbRespDesignsDescriptionTile_itemSearch
            displayUriRef
            creatorDesignTiles {
                displayName
                imageUrl
                linkData {
                    ...SeoLink_linkData
                }
            }
        }
    `,
});
